import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { cloneDeep } from "lodash-es";
import type {
  TIndividualQuestionnaireForm,
  TIndividualSurveyQuestionnaire,
  TInstitutionQuestionnaireForm,
  TInstitutionSurveyQuestionnaire,
  TKycFormV2,
  TKycInfoV2,
} from "@adgm/cross-models";
import { EKycStep, IKycStatusV2, typeList } from "@adgm/cross-models";
import {
  INDIVIDUAL_QUESTIONNAIRE_FORM,
  INSTITUTION_INFORMATION_FORM,
  INSTITUTION_QUESTIONNAIRE_FORM,
  LATEST_KYC_API_VERSION,
} from "@adgm/cross-config";
import { transformData } from "@adgm/libs";
import type { IGenericFormRef } from "./GenericForm";
import GenericForm from "./GenericForm";
import {
  INDIVIDUAL_QUESTIONNAIRE_CONFIG,
  INSTITUTION_QUESTIONNAIRE_CONFIG,
  EMPLOYMENT_STATUS_DEPENDENT_FIELDS,
  REQUIRED_EMPLOYMENT_STATUSES,
} from "./configs";

type IQuesForm = TInstitutionQuestionnaireForm | TIndividualQuestionnaireForm;

interface IProps {
  kycForm: TKycFormV2;
  kycInfo?: TKycInfoV2;
  updateKycForm: (_form: Partial<TKycFormV2>) => void;
  updateProgress: (_progress: number, _quesListLength: number) => void;
  type: typeList;
  nationality?: string;
  customerId: string;
}

export interface IKycQuestionnaireFormRef {
  checkData: () => { hasError: boolean; errMsg?: string };
}

/**
 * KYC 问卷表单组件
 *
 * 这是一个用于 KYC（了解你的客户）流程中问卷部分的跨端表单组件。
 * 支持个人和机构两种类型的问卷，提供动态表单配置、进度跟踪、数据验证等功能。
 *
 * 主要功能：
 * - 根据 KYC 类型（个人/机构）动态加载不同的问卷配置和字段
 * - 自动初始化表单数据，支持草稿保存和拒绝数据恢复
 * - 实时跟踪问卷完成进度并更新进度条
 * - 集成风险评分计算和投资经验评估
 * - 支持表单数据验证和错误提示
 * - 跨端兼容（Web/App），使用统一的表单组件
 * - 支持多语言配置和国际化
 *
 * 表单字段包括：
 * - 个人信息：国籍、政治敏感人物、年收入、净资产等
 * - 投资经验：投资经验等级、交易年限、虚拟资产投资次数等
 * - 投资偏好：预期投资期限、风险承受能力等
 * - 机构特有：业务类型、资金来源、合规要求等
 */
export const KycQuestionnaireForm = forwardRef<IKycQuestionnaireFormRef, IProps>((props, ref) => {
  const { type, updateProgress, kycInfo, updateKycForm, kycForm, customerId, nationality } = props;

  const formRef = useRef<IGenericFormRef<IQuesForm>>(null);

  // 状态管理：控制相关字段的必填状态
  const [isEmploymentFieldsRequired, setIsEmploymentFieldsRequired] = useState(false);

  // 检查employmentStatus是否需要相关字段必填
  const checkEmploymentStatusRequired = useCallback((employmentStatus: string) => {
    const isRequired = REQUIRED_EMPLOYMENT_STATUSES.includes(employmentStatus);
    setIsEmploymentFieldsRequired(isRequired);
  }, []);

  /**
   * 初始化表单数据
   * 在此对拒绝数据|草稿数据进行存储
   * @param type KYC 类型
   * @returns 初始化后的 KYC 表单数据
   */
  const initForm = useCallback(
    (type: typeList): TKycFormV2 => {
      const _institutionForm: Pick<TKycFormV2, "institutionSurveyQuestionnaire" | "institution"> =
        cloneDeep({
          institution: INSTITUTION_INFORMATION_FORM,
          institutionSurveyQuestionnaire: INSTITUTION_QUESTIONNAIRE_FORM,
        });
      const _individualQuesForm: Pick<TKycFormV2, "individualSurveyQuestionnaire"> = cloneDeep({
        individualSurveyQuestionnaire: INDIVIDUAL_QUESTIONNAIRE_FORM,
      });
      let _kycForm: Partial<TKycFormV2> = {};
      // 检查是否存在有效的KYC表单数据：
      // 1. customerId匹配当前用户
      // 2. version为V2
      if (kycForm.customerId == customerId && kycForm.version === LATEST_KYC_API_VERSION) {
        _kycForm = {
          ...(type === typeList.Individuals ? _individualQuesForm : _institutionForm),
          nationality: "",
        };
        transformData(_kycForm, kycForm);
      } else {
        // 不关注之前表单和当前页面类型是否一致,都需要初始化表单数据
        _kycForm = {
          //默认机构数据
          ..._institutionForm,
          institutionStep: EKycStep.QUESTIONNAIRE,
          nationality: "",
        };
        if (type == typeList.Individuals) {
          //个人类型 - 只初始化问卷数据，不需要个人信息
          _kycForm = {
            ..._individualQuesForm,
            individualStep: EKycStep.QUESTIONNAIRE,
            nationality: "",
          };
        }
        if (kycInfo && kycInfo.version === LATEST_KYC_API_VERSION) {
          //kyc被拒之后重新编辑,只填充最新kyc api版本的数据
          transformData(_kycForm, kycInfo);
        }
      }
      _kycForm.type = type;
      _kycForm.customerId = customerId;
      _kycForm.status = IKycStatusV2.Auditing;
      _kycForm.version = LATEST_KYC_API_VERSION;
      nationality && (_kycForm.nationality = nationality);
      updateKycForm(_kycForm as TKycFormV2);
      return _kycForm as TKycFormV2;
    },
    [kycForm, customerId, kycInfo, updateKycForm],
  );

  const { isIndividual, optionList, quesForm } = useMemo(() => {
    const _kycForm = initForm(type);
    const isIndividual = type == typeList.Individuals;
    const institutionForm: TInstitutionQuestionnaireForm = cloneDeep(
      INSTITUTION_QUESTIONNAIRE_FORM,
    );
    transformData(institutionForm, {
      ..._kycForm.institutionSurveyQuestionnaire,
      ..._kycForm.institution,
    });

    const individualForm: TIndividualQuestionnaireForm = cloneDeep(INDIVIDUAL_QUESTIONNAIRE_FORM);
    transformData(individualForm, _kycForm.individualSurveyQuestionnaire);
    const quesForm = isIndividual ? individualForm : institutionForm;
    // 根据类型选择字段配置
    const optionList = isIndividual
      ? INDIVIDUAL_QUESTIONNAIRE_CONFIG
      : INSTITUTION_QUESTIONNAIRE_CONFIG;

    // 初始化employmentStatus状态
    if (isIndividual && individualForm.supplement?.employmentStatus) {
      checkEmploymentStatusRequired(individualForm.supplement.employmentStatus);
    }

    return {
      isIndividual,
      optionList,
      quesForm,
    };
  }, []); //初始化数据的方法,不要添加依赖

  // 动态配置：根据employmentStatus状态调整字段配置
  const dynamicOptionList = useMemo(() => {
    if (!isIndividual) return optionList;

    return optionList.map(group => {
      if (group.type === "group" && group.fields) {
        return {
          ...group,
          fields: group.fields.map(field => {
            if (EMPLOYMENT_STATUS_DEPENDENT_FIELDS.includes(field.name)) {
              return {
                ...field,
                validation: [{ required: isEmploymentFieldsRequired }],
              };
            }
            return field;
          }),
        };
      }
      return group;
    });
  }, [optionList, isIndividual, isEmploymentFieldsRequired]);

  // 处理进度变化
  const handleProgressChange = useCallback(
    (progress: { completed: number; total: number }) => {
      updateProgress(progress.completed, progress.total);
    },
    [updateProgress],
  );

  /**
   * 处理表单值变化
   * @param quesForm 更新后的问卷表单数据
   */
  const handleValueChange = useCallback(
    (quesForm: IQuesForm) => {
      let form: TKycFormV2 = { ...kycForm };
      if (isIndividual) {
        form = {
          individualSurveyQuestionnaire: {
            ...kycForm.individualSurveyQuestionnaire,
            ...quesForm,
          } as TIndividualSurveyQuestionnaire,
          individualStep: EKycStep.QUESTIONNAIRE,
        };

        // 动态调整required字段：当employmentStatus选择特定值时，相关字段变为必填
        if ("supplement" in quesForm && quesForm.supplement?.employmentStatus) {
          checkEmploymentStatusRequired(quesForm.supplement.employmentStatus);
        }
      } else {
        form = {
          institutionSurveyQuestionnaire: {
            ...kycForm.institutionSurveyQuestionnaire,
            ...quesForm,
          } as TInstitutionSurveyQuestionnaire,
          institutionStep: EKycStep.QUESTIONNAIRE,
        };
      }
      updateKycForm(form);
    },
    [kycForm, isIndividual, updateKycForm],
  );

  /** 表单配置 */
  const formConfig = useMemo(
    () => ({
      config: dynamicOptionList,
      initialData: quesForm,
    }),
    [dynamicOptionList, quesForm],
  );

  /**
   * 校验表单数据
   * 验证表单中的所有字段，返回验证结果
   * @returns 验证结果，包含错误状态和错误信息
   */
  const checkData = useCallback((): { hasError: boolean } => {
    if (formRef.current) {
      return formRef.current.checkData();
    }
    return { hasError: false };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      checkData,
    }),
    [checkData],
  );

  return (
    <GenericForm
      {...formConfig}
      genericFormType="questionnaire"
      onChange={handleValueChange}
      onProgressChange={handleProgressChange}
      ref={formRef}
    />
  );
});
